// AGC: NovoLote - Screen to set up lot info (farm + name) then choose creation method
// AGC: UPDATED FLOW: Step 1 = Farm + Name → Step 2 = Choose method → Create lot → Crop suggestions
import React, { useState, useEffect } from 'react'
import { ArrowLeft, Navigation, Map as MapIcon, Sparkles, Satellite, MapPin, ChevronDown, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import type { Screen, Field } from '../../types'
import { useFarms } from '../../context'
import { useTranslation } from '../../hooks/useTranslation'
import { useKeyboardScroll } from '../../hooks/useKeyboardScroll'
import { WizardLotProgressBar } from '../wizard/WizardLotProgressBar'

interface NovoLoteProps {
  onNavigate: (screen: Screen, data?: any) => void
  onBack: () => void
  initialName?: string
  /** AGC: Pre-selected farm from voice modal (single farm or last used) */
  initialFarmId?: string
  voicePrefillArea?: number
  voicePrefillCrop?: string
  // AGC: Wizard mode props — set when rendered inside LotCreationWizard
  wizardMode?: boolean
  wizardStep?: number
  onWizardSkip?: () => void
}

/**
 * AGC: NovoLote - Two-phase lot creation entry point
 * AGC: Phase 1: Farm selection + lot name input (FIRST thing user sees)
 * AGC: Phase 2: Choose design method (GPS, Map, AI, etc.)
 * AGC: Smart defaulting: If only 1 farm, auto-select it. If multiple, default to last used.
 */
export const NovoLote: React.FC<NovoLoteProps> = ({
  onNavigate,
  onBack,
  initialName,
  initialFarmId,
  voicePrefillArea,
  voicePrefillCrop,
  wizardMode,
  wizardStep,
  onWizardSkip,
}) => {
  const { currentFarm, farms, isLoading: isFarmLoading } = useFarms()
  const { t } = useTranslation()
  const { onFocus: handleInputFocus } = useKeyboardScroll()

  const [phase, setPhase] = useState<'info' | 'method'>(initialName ? 'method' : 'info')
  const [lotName, setLotName] = useState(initialName || '')
  const [showNameError, setShowNameError] = useState(false)
  const [selectedFarm, setSelectedFarm] = useState<Field | null>(null)
  const [showFarmSelector, setShowFarmSelector] = useState(false)

  // AGC: Initialize selectedFarm: voice/chosen farm > single farm > last used (currentFarm)
  useEffect(() => {
    if (!selectedFarm && farms.length > 0) {
      if (initialFarmId) {
        const fromVoice = farms.find((f) => f.id === initialFarmId)
        if (fromVoice) {
          setSelectedFarm(fromVoice)
          return
        }
      }
      if (farms.length === 1) {
        setSelectedFarm(farms[0])
      } else if (currentFarm) {
        setSelectedFarm(currentFarm)
      } else {
        setSelectedFarm(farms[0])
      }
    }
  }, [farms, currentFarm, selectedFarm, initialFarmId])

  // AGC: If voice pre-fill provides a name, skip to method selection
  useEffect(() => {
    if (initialName && lotName && selectedFarm) {
      setPhase('method')
    }
  }, [initialName, lotName, selectedFarm])

  // AGC: Handle continue from info phase to method phase
  const handleContinueToMethod = () => {
    if (!lotName.trim()) {
      setShowNameError(true)
      return
    }
    setPhase('method')
  }

  // AGC: Navigate to creation method, passing farm + name data along
  const handleMethodSelect = (method: 'gps' | 'map' | 'ai-one-click' | 'auto-detect') => {
    // AGC: Build navigation data with farm + name info
    const lotData = {
      lotName: lotName.trim(),
      selectedFieldId: selectedFarm?.id,
      fieldId: selectedFarm?.id,
      voicePrefillArea,
      voicePrefillCrop,
    }

    if (method === 'gps') {
      onNavigate('create-with-gps', lotData)
    } else if (method === 'ai-one-click') {
      onNavigate('ai-one-click-lot', lotData)
    } else if (method === 'auto-detect') {
      onNavigate('auto-detect-fields', lotData)
    } else {
      onNavigate('polygon-map', lotData)
    }
  }

  // AGC: Phase 1: Farm selection + Lot name input
  if (phase === 'info') {
    return (
      <div className="min-h-screen app-bg">
        {/* AGC: Header */}
        <header
          className="px-4 pt-4 pb-6 sticky top-0 z-10"
          style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)', paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}
        >
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('common.cancel', 'Cancelar')}
          </button>

          <h1 className="text-white text-2xl font-bold">{t('fields.newLot', 'Novo Lote')}</h1>
          <p className="text-white/80 text-sm mt-1">
            {t('fields.newLotStep1Desc', 'Informe a fazenda e o nome do lote')}
          </p>
        </header>

        <div className="px-4 py-6 space-y-4 pb-8">
          {wizardMode ? (
            <WizardLotProgressBar currentStep={wizardStep || 1} />
          ) : (
            <>
              {/* AGC: Progress indicator - Step 1 */}
              <div className="flex gap-2 mb-2">
                <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
              <p className="text-xs text-gray-500 text-center mb-2">
                {t('fields.step1of4', 'Passo 1 de 4 — Identificação')}
              </p>
            </>
          )}

          {!wizardMode && (
          // AGC: Farm selector — hidden in wizard mode (farm already created)
          isFarmLoading ? (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-blue-700 dark:text-blue-300">{t('fields.loadingFarms', 'Carregando fazendas...')}</p>
              </div>
            </div>
          ) : farms.length > 0 ? (
            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                🏠 {t('fields.targetFarm', 'Fazenda')}
              </label>
              {farms.length === 1 ? (
                /* AGC: Single farm - show as read-only indicator */
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🏠</span>
                    <div>
                      <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">{farms[0].name}</p>
                      {farms[0].location && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400">{farms[0].location}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* AGC: Multiple farms - show dropdown selector */
                <>
                  <button
                    onClick={() => setShowFarmSelector(!showFarmSelector)}
                    className="w-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 flex items-center justify-between hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🏠</span>
                      <div className="text-left">
                        <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                          {selectedFarm?.name || t('fields.chooseFarm', 'Escolha uma fazenda')}
                        </p>
                        {selectedFarm?.location && (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400">{selectedFarm.location}</p>
                        )}
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform ${showFarmSelector ? 'rotate-180' : ''}`} />
                  </button>

                  {/* AGC: Farm Dropdown */}
                  {showFarmSelector && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
                      {farms.map((farm) => {
                        const isSelected = selectedFarm?.id === farm.id
                        const plotCount = farm.plots?.length || 0

                        return (
                          <button
                            key={farm.id}
                            onClick={() => {
                              setSelectedFarm(farm)
                              setShowFarmSelector(false)
                            }}
                            className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                              isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {isSelected && <Check className="w-4 h-4 text-emerald-600" />}
                              <div>
                                <p className={`text-sm font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                  {farm.name}
                                </p>
                                {farm.location && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{farm.location}</p>
                                )}
                              </div>
                            </div>
                            {plotCount > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-md bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-gray-300">
                                {plotCount} {plotCount === 1 ? 'lote' : 'lotes'}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null)}

          {/* AGC: Lot Name Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <MapPin className="w-4 h-4 inline mr-1.5 text-emerald-600" />
              {t('fields.lotName', 'Nome do Lote')}
            </label>
            <input
              type="text"
              value={lotName}
              onChange={(e) => {
                setLotName(e.target.value)
                if (showNameError) setShowNameError(false)
              }}
              onFocus={handleInputFocus}
              onKeyDown={(e) => {
                // AGC: Android keyboard "Go" / desktop Enter → trigger Continuar button
                if (e.key === 'Enter' && lotName.trim() && !isFarmLoading && farms.length > 0) {
                  e.preventDefault()
                  handleContinueToMethod()
                }
              }}
              enterKeyHint="go"
              placeholder={t('fields.lotNamePlaceholder', 'Ex: Talhão Norte, Área A...')}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-lg dark:bg-slate-800 dark:text-gray-100 ${
                showNameError
                  ? 'border-red-400 focus:ring-red-400 bg-red-50 dark:bg-red-900/20'
                  : 'border-gray-200 dark:border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'
              }`}
              autoFocus
            />
            {showNameError && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                ⚠️ {t('fields.lotNameRequired', 'Nome do lote é obrigatório')}
              </p>
            )}
          </div>

          {/* AGC: Inline Continuar button - right below name input, visible with keyboard */}
          <button
            onClick={handleContinueToMethod}
            disabled={!lotName.trim() || isFarmLoading || farms.length === 0}
            className="w-full py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <span>{t('fields.continueToMethod', 'Continuar')}</span>
            <ArrowLeft className="w-5 h-5 rotate-180" />
          </button>

          {wizardMode && onWizardSkip && (
            <button
              onClick={onWizardSkip}
              className="w-full text-center text-gray-500 dark:text-gray-400 text-sm mt-3 hover:text-gray-700 dark:hover:text-gray-300"
            >
              {t('fields.wizardLotSkip')}
            </button>
          )}

          {/* AGC: Info hint - below the button */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('fields.infoAfterName', 'Depois de informar o nome, você escolherá como desenhar os limites do lote.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // AGC: Phase 2: Choose creation method
  return (
    <div className="min-h-screen app-bg">
      {/* AGC: Header with lot info summary */}
      <header
        className="px-4 pt-4 pb-6 sticky top-0 z-10"
        style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)', paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}
      >
        <button
          onClick={() => setPhase('info')}
          className="flex items-center gap-2 text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.back', 'Voltar')}
        </button>

        <h1 className="text-white text-2xl font-bold">{t('fields.chooseMethod', 'Escolha o método')}</h1>
        <p className="text-white/80 text-sm mt-1">
          {t('fields.chooseMethodDesc', 'Como deseja definir os limites do lote?')}
        </p>

        {/* AGC: Summary of lot info selected in phase 1 */}
        <div className="mt-3 bg-white/10 rounded-xl px-3 py-2 flex items-center gap-3">
          <span className="text-lg">🏠</span>
          <div className="flex-1 min-w-0">
            <p className="text-white/60 text-xs">{t('fields.targetFarm', 'Fazenda')}</p>
            <p className="text-white text-sm font-medium truncate">{selectedFarm?.name || '—'}</p>
          </div>
          <div className="h-8 w-px bg-white/20" />
          <div className="flex-1 min-w-0">
            <p className="text-white/60 text-xs">{t('fields.lotName', 'Nome do Lote')}</p>
            <p className="text-white text-sm font-medium truncate">{lotName || '—'}</p>
          </div>
        </div>
      </header>

      <div className="px-4 py-6 space-y-4">
        {wizardMode ? (
          <WizardLotProgressBar currentStep={wizardStep || 2} />
        ) : (
          <>
            {/* AGC: Progress indicator - Step 2 */}
            <div className="flex gap-2 mb-2">
              <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
              <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
              <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700" />
            </div>
            <p className="text-xs text-gray-500 text-center mb-4">
              {t('fields.step2of4', 'Passo 2 de 4 — Método de criação')}
            </p>
          </>
        )}

        {/* AGC: AI One-Click Detection Button - DISABLED (coming soon) */}
        <button
          onClick={() => toast(t('fields.aiOneClickComingSoon', 'IA One-Click — em breve!'), { icon: '✨' })}
          className="relative w-full bg-gradient-to-r from-violet-600/60 via-purple-500/60 to-fuchsia-500/60 rounded-2xl shadow-lg p-5 border-2 border-transparent transition-all duration-200 active:scale-[0.98] overflow-hidden opacity-70 cursor-not-allowed"
        >
          {/* AGC: Coming Soon Badge */}
          <span className="absolute top-3 right-3 px-2.5 py-1 text-xs font-bold bg-yellow-400 text-yellow-900 rounded-full shadow-sm">
            {t('common.comingSoon', 'Em breve')}
          </span>

          <div className="flex items-center gap-4 relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400/70 to-amber-500/70 flex items-center justify-center flex-shrink-0 shadow-lg shadow-yellow-500/20">
              <Sparkles className="w-7 h-7 text-white/80 drop-shadow-md" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-bold text-white/90 mb-0.5 flex items-center gap-2">
                IA One-Click
              </h3>
              <p className="text-sm text-white/80">
                {t('fields.aiOneClickDesc', 'Toque no mapa e a IA detecta os limites automaticamente')}
              </p>
            </div>
          </div>
        </button>

        {/* AGC: Auto-Detect via NDVI Satellite Button - HIDDEN per product request (leave only IA One-Click, GPS, Draw) */}
        {false && (
          <button
            onClick={() => handleMethodSelect('auto-detect')}
            className="relative w-full bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 rounded-2xl shadow-lg p-5 border-2 border-transparent hover:shadow-xl transition-all duration-200 active:scale-[0.98] overflow-hidden group"
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-3 right-6 w-1.5 h-1.5 bg-white rounded-full animate-ping" style={{ animationDuration: '3s' }} />
              <div className="absolute bottom-3 left-10 w-1 h-1 bg-cyan-200 rounded-full animate-ping" style={{ animationDuration: '2.5s', animationDelay: '1s' }} />
            </div>
            <span className="absolute top-3 right-3 px-2.5 py-1 text-xs font-bold bg-cyan-300 text-cyan-900 rounded-full shadow-sm">
              🛰️ NDVI
            </span>
            <div className="flex items-center gap-4 relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/30 group-hover:scale-110 transition-transform">
                <Satellite className="w-7 h-7 text-white drop-shadow-md" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-white mb-0.5 flex items-center gap-2">
                  Auto-Detectar
                  <span className="text-cyan-200 text-sm">🌍</span>
                </h3>
                <p className="text-sm text-white/90">
                  {t('fields.autoDetectDesc', 'Detecta automaticamente todos os lotes via satélite NDVI')}
                </p>
              </div>
            </div>
          </button>
        )}

        {/* AGC: GPS Creation Button */}
        <button
          onClick={() => handleMethodSelect('gps')}
          className="w-full bg-white dark:bg-slate-800 rounded-2xl shadow-md p-5 border-2 border-gray-200 dark:border-slate-600 hover:border-blue-400 hover:shadow-lg transition-all duration-200 active:scale-[0.98] group"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
              <Navigation className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
                {t('fields.gpsWalk', 'Caminhar com GPS')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('fields.gpsWalkDesc', 'Caminhe pelo contorno do lote gravando o trajeto')}
              </p>
            </div>
          </div>
        </button>

        {/* AGC: Manual Map Drawing Button */}
        <button
          onClick={() => handleMethodSelect('map')}
          className="w-full bg-white dark:bg-slate-800 rounded-2xl shadow-md p-5 border-2 border-gray-200 dark:border-slate-600 hover:border-emerald-400 hover:shadow-lg transition-all duration-200 active:scale-[0.98] group"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
              <MapIcon className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
                {t('fields.drawOnMap', 'Desenhar no mapa')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('fields.drawOnMapDesc', 'Marque os pontos do lote diretamente no mapa')}
              </p>
            </div>
          </div>
        </button>

        {/* AGC: Help Text */}
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 mt-6">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-purple-800 dark:text-purple-200">
              <strong>{t('fields.recommended', 'Recomendado')}:</strong> {t('fields.recommendedAI', 'Use a IA One-Click para detectar automaticamente os limites do lote na imagem de satélite. É rápido e preciso!')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NovoLote
